/**
 * Studies (protocol library) — read-only registry listing.
 *
 * P3 (source-of-truth plan §3.1): studies are created, edited, and archived
 * only in eReg — the platform registry. This page lists the registry studies
 * visible to the caller (any study at the site appears automatically) with
 * their Intelligence document counts; "New study in eReg" deep-links to the
 * eReg create form and returns here with ?created_protocol_id.
 */
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  FolderOpen, Plus, FileText, Search,
  Building2, MapPin, X, ChevronRight
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import type { Protocol } from '../types';
import { PageHeader, fieldCls } from '../../ui';
import { eregStudyCreateLink } from '../../ui/returnTo';

const RETURN_TO = '/intelligence/protocols';

export function Protocols() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [protocols, setProtocols] = useState<Protocol[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNetwork, setSelectedNetwork] = useState<string>('all');
  const [selectedSite, setSelectedSite] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('active');

  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = ['super_admin', 'administrator'].includes(user?.role || '');

  // Return leg from "New study in eReg": open the fresh study.
  useEffect(() => {
    const createdId = searchParams.get('created_protocol_id');
    if (createdId) {
      setSearchParams({}, { replace: true });
      navigate(`/intelligence/protocols/${createdId}`, { replace: true });
    }
  }, [searchParams, setSearchParams, navigate]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Load protocols with all=true for super admin
      const protocolsRes = await api.get('/api/intelligence/protocols', {
        params: isSuperAdmin ? { all: 'true' } : {}
      });
      setProtocols(protocolsRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter protocols
  const filteredProtocols = useMemo(() => {
    return protocols.filter(p => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          p.protocol_number.toLowerCase().includes(query) ||
          p.study_nickname?.toLowerCase().includes(query) ||
          p.sponsor_name?.toLowerCase().includes(query) ||
          p.site_name?.toLowerCase().includes(query) ||
          p.network_name?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Network filter
      if (selectedNetwork !== 'all' && p.network_id !== selectedNetwork) return false;

      // Site filter
      if (selectedSite !== 'all' && p.site_id !== selectedSite) return false;

      // Status filter
      if (selectedStatus !== 'all' && p.status !== selectedStatus) return false;

      return true;
    });
  }, [protocols, searchQuery, selectedNetwork, selectedSite, selectedStatus]);

  // Get unique networks and sites from protocols for filter options
  const filterOptions = useMemo(() => {
    const uniqueNetworks = new Map<string, { id: string; name: string }>();
    const uniqueSites = new Map<string, { id: string; name: string; network_id: string }>();

    protocols.forEach(p => {
      if (p.network_id && p.network_name) {
        uniqueNetworks.set(p.network_id, { id: p.network_id, name: p.network_name });
      }
      if (p.site_id && p.site_name) {
        uniqueSites.set(p.site_id, { id: p.site_id, name: p.site_name, network_id: p.network_id || '' });
      }
    });

    return {
      networks: Array.from(uniqueNetworks.values()),
      sites: Array.from(uniqueSites.values()).filter(s =>
        selectedNetwork === 'all' || s.network_id === selectedNetwork
      ),
    };
  }, [protocols, selectedNetwork]);

  const getStatusBadge = (status: string) => {
    const styles = {
      active: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
      inactive: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
      completed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status as keyof typeof styles] || styles.inactive}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedNetwork('all');
    setSelectedSite('all');
    setSelectedStatus('active');
  };

  const hasActiveFilters = searchQuery || selectedNetwork !== 'all' || selectedSite !== 'all' || selectedStatus !== 'active';

  const newStudyLink = eregStudyCreateLink({ returnTo: RETURN_TO, returnLabel: 'Site Intelligence' });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <PageHeader
        className="mb-6"
        title="Studies"
        icon={FolderOpen}
        subtitle={isSuperAdmin ? 'All registry studies across the system' : 'Registry studies at your sites — created and managed in eReg'}
        actions={
          isAdmin && (
            <Link to={newStudyLink} className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0">
              <Plus size={16} />
              New study in eReg
            </Link>
          )
        }
      />

      {/* Filters */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2 items-center">
          {/* Search */}
          <div className="flex-1 min-w-[200px] max-w-md relative">
            <Search size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search studies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full min-h-[44px] rounded-lg border border-gray-300 bg-white py-1.5 pl-8 pr-4 text-sm text-gray-900 placeholder-gray-400 transition-colors duration-150 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400 sm:min-h-0"
            />
          </div>

          {/* Network filter */}
          {isSuperAdmin && filterOptions.networks.length > 0 && (
            <div className="flex items-center gap-2">
              <Building2 size={16} className="text-gray-400" />
              <select
                value={selectedNetwork}
                onChange={(e) => {
                  setSelectedNetwork(e.target.value);
                  setSelectedSite('all'); // Reset site when network changes
                }}
                className={fieldCls}
              >
                <option value="all">All Networks</option>
                {filterOptions.networks.map(n => (
                  <option key={n.id} value={n.id}>{n.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Site filter */}
          {isSuperAdmin && filterOptions.sites.length > 0 && (
            <div className="flex items-center gap-2">
              <MapPin size={16} className="text-gray-400" />
              <select
                value={selectedSite}
                onChange={(e) => setSelectedSite(e.target.value)}
                className={fieldCls}
              >
                <option value="all">All Sites</option>
                {filterOptions.sites.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Status filter */}
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className={fieldCls}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="completed">Completed</option>
          </select>

          {/* Clear filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-sm font-medium text-gray-500 transition-colors duration-150 hover:text-gray-700 flex items-center gap-1 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X size={14} />
              Clear filters
            </button>
          )}
        </div>

        {/* Results count */}
        <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
          Showing {filteredProtocols.length} of {protocols.length} studies
        </div>
      </div>

      {/* Study list */}
      {filteredProtocols.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center dark:bg-gray-800 dark:border-gray-700">
          <FolderOpen size={48} className="mx-auto text-gray-300 mb-4 dark:text-gray-600" />
          <h3 className="text-lg font-medium text-gray-900 mb-2 dark:text-gray-100">
            {protocols.length === 0 ? 'No studies yet' : 'No matching studies'}
          </h3>
          <p className="text-sm text-gray-500 mb-4 dark:text-gray-400">
            {protocols.length === 0
              ? 'Studies are created in eReg and appear here automatically'
              : 'Try adjusting your filters'}
          </p>
          {protocols.length === 0 && isAdmin && (
            <Link to={newStudyLink} className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0">
              <Plus size={16} />
              New study in eReg
            </Link>
          )}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredProtocols.map((protocol) => (
            <div
              key={protocol.id}
              onClick={() => navigate(`/intelligence/protocols/${protocol.id}`)}
              className="bg-white rounded-xl border border-gray-200 p-4 transition-all duration-150 hover:shadow-md hover:border-primary-300 cursor-pointer group dark:bg-gray-800 dark:border-gray-700 dark:hover:border-primary-700"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <FolderOpen size={20} className="text-primary-600 dark:text-primary-400" />
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">{protocol.protocol_number}</h3>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusBadge(protocol.status)}
                  <ChevronRight size={16} className="text-gray-400 group-hover:text-primary-600 transition-colors duration-150 dark:group-hover:text-primary-400" />
                </div>
              </div>

              {protocol.study_nickname && (
                <p className="text-sm text-gray-600 mb-2 line-clamp-2 dark:text-gray-300">{protocol.study_nickname}</p>
              )}

              {protocol.sponsor_name && (
                <p className="text-xs text-gray-500 mb-3 dark:text-gray-400">Sponsor: {protocol.sponsor_name}</p>
              )}

              {/* Network/Site labels */}
              {(protocol.network_name || protocol.site_name) && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {protocol.network_name && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/40 dark:text-green-300">
                      <Building2 size={12} />
                      {protocol.network_code || protocol.network_name}
                    </span>
                  )}
                  {protocol.site_name && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 px-2.5 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">
                      <MapPin size={12} />
                      {protocol.site_code || protocol.site_name}
                    </span>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                  <FileText size={14} />
                  <span>{protocol.document_count || 0} documents</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
